import { AppAuthRefeshTokenKey, LocaleKey, AppAuthTokenKey, SelectedStoreIdKey, StoreTimezoneKey } from '@/libs/constant';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { boot } from 'quasar/wrappers';
import { Cookies } from 'quasar';
import { useAuthenStore } from '@/stores/authenStore';
import { AUTH_ROUTE_NAME } from '@/libs/route-name';
import { isClient } from '@/utils/appUtil';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance;
    $api: AxiosInstance;
  }
}

const api = axios.create({
  // baseURL: process.env.NODE_ENV == 'development' ? 'http://192.168.7.249:8080' : 'https://api.example.com',
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  withCredentials: false,
  timeout: import.meta.env.VITE_APP_TIMEOUT
    ? +import.meta.env.VITE_APP_TIMEOUT
    : 3 * 60000, // 60000 = 1 minute, 0 = no timeout
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
    'Accept-Apiclient': import.meta.env.VITE_APP_CLIENT,
  },
  validateStatus: (status) => status < 400, // Resolve only if the status code is less than 400
  // validateStatus: (status) => status <= 500 // Resolve only if the status code is less than 500
});


// for multiple requests
let isRefreshing = false;
let failedQueue: any[] = [];
const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export default boot(({ app, redirect, store, router }) => {
  const authenStore = useAuthenStore(store);
  // Check if interceptors are already set up
  const isServer = process.env.SERVER;
  if (!isServer) {
    api.interceptors.request.use(
      async (config) => {
        // Add Authorization header to all requests
        const token = Cookies.get(AppAuthTokenKey);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Accept-Language'] = Cookies.get(LocaleKey);

        if (isClient()) {
          const storeId = localStorage.getItem(SelectedStoreIdKey);
          if (storeId) {
            config.headers['X-STORE-ID'] = storeId;
          }

          const timezone = localStorage.getItem(StoreTimezoneKey);
          if (timezone) {
            config.headers['X-TIMEZONE'] = timezone;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const refreshToken = Cookies.get(AppAuthRefeshTokenKey);
        // Skip refresh logic for refresh token endpoint itself
        if (originalRequest.url?.includes('/api/v1/auth/refresh-token')) {
          router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
          return Promise.reject(error);
        }

        if (
          refreshToken &&
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          // Handle multiple concurrent requests with queue
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = 'Bearer ' + token;
                return api(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;


          router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
          return new Promise(async (resolve, reject) => {
            try {
              const refreshApi = axios.create({
                baseURL: process.env.APP_BASE_API || '',
                timeout: 30000,
                headers: {
                  'Content-Type': 'application/json',
                  'Accept-Apiclient': process.env.APP_API_CLIENT,
                },
              });

              const response = await refreshApi.post('/api/v1/auth/refresh-token', {
                refreshToken,
              });

              let tokenData;
              if (response.data && response.data.data && response.data.data.token) {
                // Format: { data: { token: { accessToken, refreshToken } } }
                tokenData = response.data.data.token;
              } else if (response.data && response.data.accessToken && response.data.refreshToken) {
                // Format: { accessToken, refreshToken }
                tokenData = response.data;
              } else {
                throw new Error('Invalid refresh token response format');
              }

              if (tokenData.accessToken && tokenData.refreshToken) {
                await authenStore.setRefreshTokenCookie(undefined, tokenData);
                originalRequest.headers.Authorization = 'Bearer ' + tokenData.accessToken;
                processQueue(null, tokenData.accessToken);
                resolve(api(originalRequest));
              } else {
                throw new Error('Missing accessToken or refreshToken in response');
              }
            } catch (errRefresh) {
              processQueue(errRefresh, null);

              authenStore.logout();
              Cookies.remove(AppAuthTokenKey);
              Cookies.remove(AppAuthRefeshTokenKey);
              router.push({ name: AUTH_ROUTE_NAME.SIGNIN });

              reject(errRefresh);
            } finally {
              isRefreshing = false;
            }
          });
        }

        // If no refresh token or different error, redirect to auth on 401
        if (error.response && error.response.status === 401) {
          authenStore.logout();
          Cookies.remove(AppAuthTokenKey);
          Cookies.remove(AppAuthRefeshTokenKey);
          router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
        }

        return Promise.reject(error);
      },
    );
  }
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;
});

export { api };
